/**
 * HR Employee API (프론트)
 *
 * 백엔드 `/api/hr/employees/me` 계열 엔드포인트를 호출하는 얇은 래퍼.
 * 서버의 snake_case ↔ 프론트의 camelCase/TS 인터페이스 어댑터 포함.
 *
 * 중요: apiClient 의 baseURL 이 이미 `/api` 이므로 경로에 `/api` 붙이면 안 됨.
 */

import api from '../api/apiClient';
import type {
  ProfileData,
  WorkInfo,
  BasicInfo,
  GrowthEntry,
  Certificate,
  Language,
} from '../../mocks/hr/profile';

// ═══════════════════════════════════════════════
// 서버 응답 스키마 (snake_case)
// ═══════════════════════════════════════════════
interface ServerEmployee {
  id: string;
  user_id: string;
  employee_number: string;
  display_name: string;
  role_title: string;
  badge_text: string;
  avatar_bg: string;
  initials: string;
  real_name: string;
  nickname: string;
  name_en: string;
  work_email: string;
  personal_email: string;
  email_verified: boolean;
  hire_date: string | null;          // Sprint 5: text → date (null 허용)
  group_hire_date: string | null;    // Sprint 5: text → date
  hire_type: string;
  ssn: string;
  birthday: string;
  nationality: string;
  residence: string;
  emergency_contact: string;
  address: string;
  current_status: 'active' | 'leave' | 'resigned';  // Sprint 5
  status_changed_at: string | null;                 // Sprint 5
  created_at: string;
  updated_at: string;
}

interface ServerGrowth {
  id: string;
  employee_id: string;
  period: string;
  title: string;
  description: string;
  chips: string[];
  sort_index: number;
  created_at: string;
  updated_at: string;
}

interface ServerCert {
  id: string;
  employee_id: string;
  name: string;
  acquired_date: string;
  grade: string;
  issuer: string;
  expiry_date: string;
  sort_index: number;
}

interface ServerLang {
  id: string;
  employee_id: string;
  name: string;
  level: string;
  sort_index: number;
}

interface ServerAssignment {
  userPosition: string;
  profileImageUrl?: string;
  teamId: string | null;
  teamName: string;
  roleInTeam: string;
  positionLevel: number | null;
}

interface ServerComposite {
  employee: ServerEmployee;
  growth_entries: ServerGrowth[];
  certifications: ServerCert[];
  languages: ServerLang[];
  assignment: ServerAssignment;
}

/** 프론트에서 편집/표시하는 근무 배정 정보 */
export interface Assignment {
  userPosition: string;        // users.position (본인 편집)
  profileImageUrl: string;     // users.profile_image_url (본인 편집)
  teamId: string | null;       // primaryTeam.id (읽기)
  teamName: string;            // primaryTeam.name
  roleInTeam: string;          // team_members.role_in_team
  positionLevel: number | null; // team_members.position_level
}

export const EMPTY_ASSIGNMENT: Assignment = {
  userPosition: '',
  profileImageUrl: '',
  teamId: null,
  teamName: '',
  roleInTeam: '',
  positionLevel: null,
};

export function toAssignment(a: ServerAssignment | undefined | null): Assignment {
  if (!a) return { ...EMPTY_ASSIGNMENT };
  return {
    userPosition: a.userPosition || '',
    profileImageUrl: a.profileImageUrl || '',
    teamId: a.teamId ?? null,
    teamName: a.teamName || '',
    roleInTeam: a.roleInTeam || '',
    positionLevel: a.positionLevel ?? null,
  };
}

// ─── Sprint 2: 목록 · 트리 ─────────────────────────────────────────
export interface EmployeeTeam {
  id: string;
  code: string;
  name: string;
  roleInTeam: 'leader' | 'deputy' | 'member' | 'observer' | string;
  positionLevel: number;
}

export interface EmployeeRow {
  userId: string;
  name: string;
  email: string;
  department: string;
  position: string;
  role: string;
  isActive: boolean;
  phone: string;
  profileImageUrl: string;
  avatarInitials: string;
  employeeNumber: string;
  // Sprint 5
  currentStatus: 'active' | 'leave' | 'resigned';
  hireDate: string | null;
  primaryTeam: (EmployeeTeam & { isLeader: boolean }) | null;
  teams: EmployeeTeam[];
}

export interface EmployeeListParams {
  q?: string;
  teamId?: string;
  roleInTeam?: 'leader' | 'deputy' | 'member';
  status?: 'active' | 'inactive' | 'all';
  currentStatus?: 'active' | 'leave' | 'resigned' | 'all';
  limit?: number;
  offset?: number;
  // Sprint 6D — 배열·범위 필터
  teamIds?: string[];
  rolesInTeam?: Array<'leader' | 'deputy' | 'member' | 'observer'>;
  currentStatuses?: Array<'active' | 'leave' | 'resigned'>;
  positionLevelMin?: number;
  positionLevelMax?: number;
  hiredFrom?: string; // YYYY-MM-DD
  hiredTo?: string;
  /**
   * 퇴사자 포함 여부 (2026-08-04). 기본 false — 보내지 않으면 서버가 퇴사자를 제외한다.
   * true 를 보내도 퇴사자 열람 권한이 없으면 서버가 무시한다.
   */
  includeResigned?: boolean;
}

/** Sprint 6D — 쿼리 파라미터 빌더 (list + export 공용) */
function buildEmployeeQuery(params: EmployeeListParams): URLSearchParams {
  const q = new URLSearchParams();
  if (params.q) q.set('q', params.q);
  if (params.teamId) q.set('team_id', params.teamId);
  if (params.roleInTeam) q.set('role_in_team', params.roleInTeam);
  if (params.status) q.set('status', params.status);
  if (params.currentStatus) q.set('current_status', params.currentStatus);
  if (params.limit !== undefined) q.set('limit', String(params.limit));
  if (params.offset !== undefined) q.set('offset', String(params.offset));
  if (params.teamIds?.length) q.set('team_ids', params.teamIds.join(','));
  if (params.rolesInTeam?.length) q.set('roles_in_team', params.rolesInTeam.join(','));
  if (params.currentStatuses?.length) q.set('current_statuses', params.currentStatuses.join(','));
  if (params.positionLevelMin != null) q.set('position_level_min', String(params.positionLevelMin));
  if (params.positionLevelMax != null) q.set('position_level_max', String(params.positionLevelMax));
  if (params.hiredFrom) q.set('hired_from', params.hiredFrom);
  if (params.hiredTo) q.set('hired_to', params.hiredTo);
  // 퇴사자 포함은 «켤 때만» 보낸다 (기본 미노출 — 파라미터 부재 = 제외)
  if (params.includeResigned) q.set('includeResigned', 'true');
  return q;
}

/**
 * Sprint 6D — CSV 다운로드 (blob 기반, 인증 헤더 사용)
 * 브라우저에서 호출 시 자동으로 파일 다운로드를 트리거.
 */
export async function downloadEmployeesCsv(params: EmployeeListParams): Promise<void> {
  const q = buildEmployeeQuery(params);
  const qs = q.toString();
  const url = `/hr/employees/export.csv${qs ? `?${qs}` : ''}`;
  const blob = await api.get<Blob>(url, { responseType: 'blob' });
  const today = new Date().toISOString().slice(0, 10);
  const filename = `hr-employees-${today}.csv`;
  const objUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(objUrl);
}

// Sprint 5: HR 대시보드 KPI
export interface DashboardKpi {
  totalMembers: number;
  onLeave: number;
  resigned: number;
  hiredThisMonth: number;
  birthdaysThisMonth: number;
  /** Sprint 6A — 이번 달 입사 기념일자 (올해 이전 입사, 월 일치) */
  anniversariesThisMonth: number;
  period: {
    year: number;
    month: number;
    monthStart: string;
    monthEnd: string;
  };
}

/** Sprint 6A — 대시보드 차트 데이터 */
export interface TeamDistributionRow {
  teamId: string;
  teamName: string;
  count: number;
}
export interface TenureDistributionRow {
  key: string;   // '<1y' | '1-3y' | '3-5y' | '5-10y' | '10y+'
  label: string; // '1년 미만' 등 표시용
  count: number;
}
export interface DashboardCharts {
  teamDistribution: TeamDistributionRow[];
  tenureDistribution: TenureDistributionRow[];
}

// ═══════════════════════════════════════════════
// 어댑터: 서버(snake) → 프론트(camel/interface)
// ═══════════════════════════════════════════════
export function toProfileData(e: ServerEmployee): ProfileData {
  return {
    name: e.display_name,
    emp: e.employee_number,
    role: e.role_title,
    squad: '',
    badge: e.badge_text,
    avatarBg: e.avatar_bg,
    initials: e.initials,
    mode: 'me',
  };
}

/**
 * Sprint 5: work_team/work_job/work_level/work_updated_at 컬럼 drop
 * WorkInfo 는 호환 유지를 위해 유지하되, 모든 필드는 빈 값으로.
 * (기존 UI 가 work.updatedAt 을 참조하지 않도록 MemberProfilePage 에서 updatedAt 사용 중단됨)
 */
export function toWorkInfo(_: ServerEmployee): WorkInfo {
  return { team: '', job: '', level: '', updatedAt: '' };
}

export function toBasicInfo(e: ServerEmployee): BasicInfo {
  return {
    realName: e.real_name,
    nickname: e.nickname,
    nameEn: e.name_en,
    workEmail: e.work_email,
    personalEmail: e.personal_email,
    emailVerified: e.email_verified,
    // Sprint 5: date 타입으로 전환 — null 일 때 빈 문자열로 반환 (UI 하위 호환)
    hireDate: e.hire_date || '',
    groupHireDate: e.group_hire_date || '',
    hireType: e.hire_type,
    ssn: e.ssn,
    birthday: e.birthday,
    nationality: e.nationality,
    residence: e.residence,
    emergencyContact: e.emergency_contact,
    address: e.address,
  };
}

export function toGrowthEntry(g: ServerGrowth): GrowthEntry {
  return {
    id: g.id,
    date: g.period,
    title: g.title,
    desc: g.description,
    chips: Array.isArray(g.chips) ? g.chips : [],
  };
}

export function toCertificate(c: ServerCert): Certificate {
  return {
    id: c.id,
    name: c.name,
    acquiredDate: c.acquired_date,
    grade: c.grade,
    issuer: c.issuer,
    expiryDate: c.expiry_date,
  };
}

export function toLanguage(l: ServerLang): Language {
  return { id: l.id, name: l.name, level: l.level };
}

// ═══════════════════════════════════════════════
// 어댑터: 프론트 → 서버 patch (snake_case)
// ═══════════════════════════════════════════════
export function buildEmployeePatch(
  profile: Partial<ProfileData>,
  _work: Partial<WorkInfo>,
  basic: Partial<BasicInfo>,
): Partial<ServerEmployee> {
  const patch: Partial<ServerEmployee> = {};
  if (profile.name !== undefined) patch.display_name = profile.name;
  if (profile.emp !== undefined) patch.employee_number = profile.emp;
  if (profile.role !== undefined) patch.role_title = profile.role;
  if (profile.badge !== undefined) patch.badge_text = profile.badge;
  if (profile.avatarBg !== undefined) patch.avatar_bg = profile.avatarBg;
  if (profile.initials !== undefined) patch.initials = profile.initials;

  // Sprint 5: work_team/work_job/work_level 컬럼 drop — 파라미터는 무시
  // (_work 는 호환성을 위해 signature 유지)

  if (basic.realName !== undefined) patch.real_name = basic.realName;
  if (basic.nickname !== undefined) patch.nickname = basic.nickname;
  if (basic.nameEn !== undefined) patch.name_en = basic.nameEn;
  if (basic.workEmail !== undefined) patch.work_email = basic.workEmail;
  if (basic.personalEmail !== undefined) patch.personal_email = basic.personalEmail;
  if (basic.emailVerified !== undefined) patch.email_verified = basic.emailVerified;
  // Sprint 5: date 타입 — 빈 문자열은 서버에서 null 로 변환
  if (basic.hireDate !== undefined) patch.hire_date = (basic.hireDate || null) as any;
  if (basic.groupHireDate !== undefined) patch.group_hire_date = (basic.groupHireDate || null) as any;
  if (basic.hireType !== undefined) patch.hire_type = basic.hireType;
  if (basic.ssn !== undefined) patch.ssn = basic.ssn;
  if (basic.birthday !== undefined) patch.birthday = basic.birthday;
  if (basic.nationality !== undefined) patch.nationality = basic.nationality;
  if (basic.residence !== undefined) patch.residence = basic.residence;
  if (basic.emergencyContact !== undefined) patch.emergency_contact = basic.emergencyContact;
  if (basic.address !== undefined) patch.address = basic.address;
  return patch;
}

// ═══════════════════════════════════════════════
// API 호출
// ═══════════════════════════════════════════════
export interface MyProfileComposite {
  profile: ProfileData;
  work: WorkInfo;
  basic: BasicInfo;
  growth: GrowthEntry[];
  certs: Certificate[];
  langs: Language[];
  assignment: Assignment;
  /** Sprint 5: employees.current_status (재직/휴직/퇴사) */
  currentStatus: 'active' | 'leave' | 'resigned';
}

// api.get<T>, api.post<T, D>, api.patch<T, D>, api.delete<T> 모두 response.data 를 unwrap 해서 Promise<T> 반환
type Envelope<T> = { success: boolean; data: T; error?: string };

export const hrEmployeeApi = {
  async getMe(): Promise<MyProfileComposite> {
    const res = await api.get<Envelope<ServerComposite>>('/hr/employees/me');
    const src = res.data;
    return {
      profile: toProfileData(src.employee),
      work: toWorkInfo(src.employee),
      basic: toBasicInfo(src.employee),
      growth: (src.growth_entries || []).map(toGrowthEntry),
      certs: (src.certifications || []).map(toCertificate),
      langs: (src.languages || []).map(toLanguage),
      assignment: toAssignment(src.assignment),
      currentStatus: (src.employee.current_status || 'active') as 'active' | 'leave' | 'resigned',
    };
  },

  async patchMe(payload: Partial<ServerEmployee>): Promise<ServerEmployee> {
    const res = await api.patch<Envelope<ServerEmployee>, Partial<ServerEmployee>>(
      '/hr/employees/me',
      payload,
    );
    return res.data;
  },

  /**
   * Sprint 5: admin 이 타 구성원의 employees 필드 수정.
   * PATCH /api/hr/employees/:userId (admin/superadmin 권한)
   */
  async patchAsAdmin(userId: string, payload: Partial<ServerEmployee>): Promise<ServerEmployee> {
    const res = await api.patch<Envelope<ServerEmployee>, Partial<ServerEmployee>>(
      `/hr/employees/${userId}`,
      payload,
    );
    return res.data;
  },

  // ── growth ─────────────────────────────────
  growth: {
    async list(): Promise<GrowthEntry[]> {
      const res = await api.get<Envelope<ServerGrowth[]>>('/hr/employees/me/growth');
      return (res.data || []).map(toGrowthEntry);
    },
    async create(entry: Omit<GrowthEntry, 'id'>): Promise<GrowthEntry> {
      const body = {
        period: entry.date,
        title: entry.title,
        description: entry.desc,
        chips: entry.chips || [],
      };
      const res = await api.post<Envelope<ServerGrowth>, typeof body>('/hr/employees/me/growth', body);
      return toGrowthEntry(res.data);
    },
    async update(id: string, entry: Partial<GrowthEntry>): Promise<GrowthEntry> {
      const body: Partial<ServerGrowth> = {};
      if (entry.date !== undefined) body.period = entry.date;
      if (entry.title !== undefined) body.title = entry.title;
      if (entry.desc !== undefined) body.description = entry.desc;
      if (entry.chips !== undefined) body.chips = entry.chips;
      const res = await api.patch<Envelope<ServerGrowth>, Partial<ServerGrowth>>(
        `/hr/employees/me/growth/${id}`,
        body,
      );
      return toGrowthEntry(res.data);
    },
    async remove(id: string): Promise<void> {
      await api.delete(`/hr/employees/me/growth/${id}`);
    },
  },

  // ── certifications ─────────────────────────
  certs: {
    async list(): Promise<Certificate[]> {
      const res = await api.get<Envelope<ServerCert[]>>('/hr/employees/me/certifications');
      return (res.data || []).map(toCertificate);
    },
    async create(c: Omit<Certificate, 'id'>): Promise<Certificate> {
      const body = {
        name: c.name,
        acquired_date: c.acquiredDate,
        grade: c.grade,
        issuer: c.issuer,
        expiry_date: c.expiryDate,
      };
      const res = await api.post<Envelope<ServerCert>, typeof body>('/hr/employees/me/certifications', body);
      return toCertificate(res.data);
    },
    async update(id: string, c: Partial<Certificate>): Promise<Certificate> {
      const body: Partial<ServerCert> = {};
      if (c.name !== undefined) body.name = c.name;
      if (c.acquiredDate !== undefined) body.acquired_date = c.acquiredDate;
      if (c.grade !== undefined) body.grade = c.grade;
      if (c.issuer !== undefined) body.issuer = c.issuer;
      if (c.expiryDate !== undefined) body.expiry_date = c.expiryDate;
      const res = await api.patch<Envelope<ServerCert>, Partial<ServerCert>>(
        `/hr/employees/me/certifications/${id}`,
        body,
      );
      return toCertificate(res.data);
    },
    async remove(id: string): Promise<void> {
      await api.delete(`/hr/employees/me/certifications/${id}`);
    },
  },

  // ── languages ──────────────────────────────
  langs: {
    async list(): Promise<Language[]> {
      const res = await api.get<Envelope<ServerLang[]>>('/hr/employees/me/languages');
      return (res.data || []).map(toLanguage);
    },
    async create(l: Omit<Language, 'id'>): Promise<Language> {
      const body = { name: l.name, level: l.level };
      const res = await api.post<Envelope<ServerLang>, typeof body>('/hr/employees/me/languages', body);
      return toLanguage(res.data);
    },
    async update(id: string, l: Partial<Language>): Promise<Language> {
      const body: Partial<ServerLang> = {};
      if (l.name !== undefined) body.name = l.name;
      if (l.level !== undefined) body.level = l.level;
      const res = await api.patch<Envelope<ServerLang>, Partial<ServerLang>>(
        `/hr/employees/me/languages/${id}`,
        body,
      );
      return toLanguage(res.data);
    },
    async remove(id: string): Promise<void> {
      await api.delete(`/hr/employees/me/languages/${id}`);
    },
  },

  // ─── Sprint 2: 전 구성원 목록 + 타 구성원 프로필 ───────────────
  /**
   * GET /api/hr/employees — 구성원 목록 (users+teams join)
   * canViewResigned: 「퇴사자 포함」 토글을 화면에 렌더할지 판단용 (권한 보유 ≠ 기본 노출)
   */
  async list(params: EmployeeListParams = {}): Promise<{
    rows: EmployeeRow[];
    total: number;
    canViewResigned: boolean;
  }> {
    const query = buildEmployeeQuery(params);
    const qs = query.toString();
    const res = await api.get<{
      success: boolean;
      data: EmployeeRow[];
      metadata?: { total?: number; canViewResigned?: boolean };
    }>(`/hr/employees${qs ? `?${qs}` : ''}`);
    return {
      rows: res.data || [],
      total: res.metadata?.total ?? (res.data || []).length,
      canViewResigned: res.metadata?.canViewResigned === true,
    };
  },

  // ─── Sprint 5: 재직 상태 변경 + 대시보드 KPI ────────────────────
  /**
   * PATCH /api/hr/employees/:userId/status — admin 전용
   * @param userId 대상 사용자 id
   * @param status 'active'|'leave'|'resigned'
   */
  async updateStatus(
    userId: string,
    status: 'active' | 'leave' | 'resigned',
  ): Promise<void> {
    await api.patch<{ success: boolean }, { status: string }>(
      `/hr/employees/${userId}/status`,
      { status },
    );
  },

  /** GET /api/hr/dashboard/kpi — 대시보드 KPI */
  async getKpi(): Promise<DashboardKpi> {
    const res = await api.get<{ success: boolean; data: DashboardKpi }>(
      '/hr/dashboard/kpi',
    );
    return res.data;
  },

  /** GET /api/hr/dashboard/charts — 팀별 인원 + 근속 연수 분포 (Sprint 6A) */
  async getCharts(): Promise<DashboardCharts> {
    const res = await api.get<{ success: boolean; data: DashboardCharts }>(
      '/hr/dashboard/charts',
    );
    return res.data;
  },

  /** GET /api/hr/employees/:userId — 타 구성원 composite (권한별 마스킹) */
  async byUserId(userId: string): Promise<MyProfileComposite> {
    const res = await api.get<Envelope<ServerComposite>>(`/hr/employees/${userId}`);
    const src = res.data;
    return {
      profile: toProfileData(src.employee),
      work: toWorkInfo(src.employee),
      basic: toBasicInfo(src.employee),
      growth: (src.growth_entries || []).map(toGrowthEntry),
      certs: (src.certifications || []).map(toCertificate),
      langs: (src.languages || []).map(toLanguage),
      assignment: toAssignment(src.assignment),
      currentStatus: (src.employee.current_status || 'active') as 'active' | 'leave' | 'resigned',
    };
  },

  // ─── Sprint 3: 직무 · 직책 · 직급 저장 ────────────────────────
  /** PUT /api/users/me/profile — 본인 직무(users.position) 수정 */
  async updateMyPosition(position: string): Promise<void> {
    await api.put<{ success: boolean }, { position: string }>('/users/me/profile', { position });
  },

  /**
   * Sprint 5: admin 이 타 구성원의 users.position 수정.
   * PUT /api/admin/users/:id (admin/superadmin 권한)
   */
  async updateUserPositionAsAdmin(userId: string, position: string): Promise<void> {
    await api.put<{ success: boolean }, { position: string }>(
      `/admin/users/${userId}`,
      { position },
    );
  },

  /**
   * PATCH /api/admin/teams/:teamId/members/:userId — team_members 역할/직급 수정
   *
   * admin/superadmin 권한 필요. 일반 사용자는 403 — UI 에서 사전 가드.
   */
  async updateTeamMemberRole(
    teamId: string,
    userId: string,
    body: { role_in_team?: string; position_level?: number },
  ): Promise<void> {
    await api.patch<{ success: boolean }, typeof body>(
      `/admin/teams/${teamId}/members/${userId}`,
      body,
    );
  },

  /**
   * PUT /api/hr/employees/:userId/team — 주 소속팀 변경 (2026-07-21)
   *
   * admin/superadmin 권한 필요. teamId=null 이면 무소속 처리.
   * 백엔드가 기존 활성 team_members 를 SCD-2 종료 후 새 멤버십을 생성하므로
   * 조직도(GET /api/hr/org-chart)에 즉시 반영된다.
   */
  async updateTeam(userId: string, teamId: string | null): Promise<void> {
    await api.put<{ success: boolean }, { team_id: string | null }>(
      `/hr/employees/${userId}/team`,
      { team_id: teamId },
    );
  },

  // ─── Sprint 4: 프로필 사진 ──────────────────────────────────
  /**
   * POST /api/users/me/profile/avatar (Sprint 6B)
   * Base64 data URL → Supabase Storage avatars 버킷 업로드 → public URL 을
   *                   users.profile_image_url 에 저장. 반환된 URL 을 프론트 state 에 반영.
   *
   * @param dataUrl  `data:image/(jpeg|png|webp);base64,...` (5MB 상한)
   * @returns 업로드된 public URL
   */
  async updateMyProfileImage(dataUrl: string): Promise<string> {
    if (!dataUrl) {
      await hrEmployeeApi.deleteMyProfileImage();
      return '';
    }
    const res = await api.post<
      { success: boolean; data: { url: string; path: string; size: number } },
      { data_url: string }
    >('/users/me/profile/avatar', { data_url: dataUrl });
    return res.data?.url ?? '';
  },

  /**
   * DELETE /api/users/me/profile/avatar (Sprint 6B)
   * Storage object 삭제 + users.profile_image_url = null
   */
  async deleteMyProfileImage(): Promise<void> {
    await api.delete<{ success: boolean }>('/users/me/profile/avatar');
  },
};

export default hrEmployeeApi;
